import "./TimeSeriesWidgetComponent.scss";
import template from "./TimeSeriesWidgetComponent.html";
import {IComponentOptions, IOnDestroy} from "angular";
import {CodeAndExchange} from "../../../../../instruments/types/CodeAndExchange";
import {Observable, ReplaySubject, Subject} from "rxjs";
import {ComponentCallback} from "../../../../../types/AngularJS";
import {defaultConfig, TimeSeriesConfigUpdate, TimeSeriesWidgetConfig} from "./models/TimeSeriesWidgetConfig";
import {isNotNull} from "../../../../../js/util/UtilFunctions";
import {ObjectUtil} from "../../../../../js/util/ObjectUtil";
import {TimeSeriesPeriod} from "../../../../../security/time-series/types/TimeSeriesPeriod";
import {WidgetDisplayMode} from "../../../models/WidgetDisplayMode";

export const TimeSeriesWidget = "tfTimeSeriesWidget";

export function timeSeriesWidget(): IComponentOptions {
    return {
        template,
        bindings: {
            security: "<",
            config: "<",
            displayMode: "<",
            onSecurityChanged: "&",
            onConfigChanged: "&"
        },
        controllerAs: "ctrl",
        controller: TimeSeriesWidgetComponentController
    };
}

export class TimeSeriesWidgetComponentController implements IOnDestroy {

    /** @input */
    public set security(security: CodeAndExchange | null) {
        if (security != null) {
            this.security$.next(security);
        }
    }

    private readonly security$ = new ReplaySubject<CodeAndExchange>(1);

    /** @input */
    public set config(config: TimeSeriesConfigUpdate | null) {
        if (config != null) {
            this.config$.next(config);
        } else {
            this.config$.next(defaultConfig());
        }
    }

    private readonly config$ = new ReplaySubject<TimeSeriesConfigUpdate>(1);

    /** @input */
    public set displayMode(displayMode: WidgetDisplayMode | null) {
        if (displayMode != null) {
            this.displayMode$.next(displayMode);
        }
    }

    private readonly displayMode$ = new ReplaySubject<WidgetDisplayMode>(1);

    /** @output */
    public readonly onSecurityChanged: ComponentCallback;

    /** @output */
    public readonly onConfigChanged: ComponentCallback;

    public readonly currentSecurity$: Observable<CodeAndExchange>;
    public readonly currentPeriod$: Observable<TimeSeriesPeriod>;
    public readonly currentAdjusted$: Observable<boolean>;

    private readonly changes$ = new Subject<TimeSeriesConfigUpdate>();
    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    /** @ngInject */
    public constructor() {
        this.currentSecurity$ = this.buildCurrentSecurity();
        this.currentPeriod$ = this.buildCurrentPeriod();
        this.currentAdjusted$ = this.buildCurrentAdjusted();
        this.watchConfigChanges();
        this.watchSecurity();
    }

    private buildCurrentSecurity(): Observable<CodeAndExchange> {
        return this.config$
                   .filter(isNotNull)
                   .map((c) => c.security)
                   .filter(isNotNull)
                   .publishReplay(1)
                   .refCount();
    }

    private buildCurrentPeriod(): Observable<TimeSeriesPeriod> {
        return this.config$
                   .filter(isNotNull)
                   .map((c) => c.period)
                   .filter(isNotNull)
                   .publishReplay(1)
                   .refCount();
    }

    private buildCurrentAdjusted(): Observable<boolean> {
        return this.config$
                   .filter(isNotNull)
                   .map((c) => c.adjusted)
                   .filter(isNotNull)
                   .startWith(true)
                   .publishReplay(1)
                   .refCount();
    }

    private watchConfigChanges() {
        this.config$
            .takeUntil(this.destroyed$)
            .switchMap((config) => this.watchChanges(config))
            .auditTime(100)
            .subscribe((config) => this.onConfigChanged({config}));
    }

    private watchChanges(initial: TimeSeriesWidgetConfig): Observable<TimeSeriesWidgetConfig> {
        return this.changes$.scan((acc, next) => this.updateConfig(next, acc), initial);
    }

    private updateConfig(change: TimeSeriesConfigUpdate,
                         config: TimeSeriesWidgetConfig | null): TimeSeriesWidgetConfig {
        return {
            ...config,
            ...change
        };
    }

    private watchSecurity() {
        this.security$
            .takeUntil(this.destroyed$)
            .subscribe((security) => this.changes$.next({security}));
    }

    public securityChanged(security: CodeAndExchange) {
        const cae = ObjectUtil.pick(security, "code", "exchange");
        this.changes$.next({security: cae});
        this.onSecurityChanged({security: cae});
    }

    public updatePeriod(period: TimeSeriesPeriod) {
        this.changes$.next({period});
    }

    public updateAdjusted(adjusted: boolean) {
        this.changes$.next({adjusted: !adjusted});
    }

    public $onDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
